import * as React from 'react';
import { useState } from 'react';
import * as API from '../../Utils/API';
import { htmlIf, maybeHtml } from "../../Utils/HTML";
import { CopilotProfileProps } from './CopilotProfile';
import { DismissibleModal } from '../../Components/Modal';
import { CopilotEducationLine } from '../../Types/Copilot';
import PaginatedItems from '../../Components/PaginatedItems';
import MonthYearSelect from '../../Components/MonthYearSelect';

// Courtesy of ChatGPT
function sortEducationHistory(educationLines: CopilotEducationLine[]) {
  return educationLines.sort((a, b) => {
    // Handle ongoing jobs (undefined end date) — they should come first
    if (!a.endYear && !b.endYear) {
      // If both are ongoing, sort by earliest start date
      return compareStartDates(a, b);
    }
    if (!a.endYear) return -1; // a is ongoing, so it comes first
    if (!b.endYear) return 1;  // b is ongoing, so a comes first

    // Compare end dates (latest first)
    if (b.endYear !== a.endYear) return b.endYear - a.endYear;
    if (b.endMonth && a.endMonth && b.endMonth !== a.endMonth) {
      return b.endMonth - a.endMonth;
    }

    // If end dates are the same, compare start dates (earliest first)
    return compareStartDates(a, b);
  });
}

// Courtesy of ChatGPT
function compareStartDates(a: CopilotEducationLine, b: CopilotEducationLine) {
  if (!a.startYear || !b.startYear) return 0; // If missing start date, keep order
  if (a.startYear !== b.startYear) return a.startYear - b.startYear;
  if (a.startMonth && b.startMonth) return a.startMonth - b.startMonth;
  return 0;
}

// Courtesy of ChatGPT
const formatEducationDates = ({ startMonth, startYear, endMonth, endYear }: CopilotEducationLine): string => {
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const format = (month?: number, year?: number) => year ? `${month ? months[month - 1] + " " : ""}${year}` : "Present";

  return `${format(startMonth, startYear)} – ${format(endMonth, endYear)}`;
};

export const ReadOnly = (props: CopilotProfileProps) => (
  <div className="card mt-3 rounded-2">
    <div className="card-body py-3">
      <div className="fs-xl fw-bold text-dark">Education history</div>
      <div className="mt-2">
        {props.educationHistory.length >= 5
        ? <PaginatedItems
            allItems={sortEducationHistory(props.educationHistory)}
            numItemsPerPage={5}
            renderItem={(line) => (
              <div className="py-2 border-bottom" key={`education-line-${line.id}`}>
                {RenderLineContent(line)}
              </div>
            )}
          />
        : sortEducationHistory(props.educationHistory).map((line) => (
            <div className="py-2 border-bottom" key={`education-line-${line.id}`}>
              {RenderLineContent(line)}
            </div>
          ))
        }
      </div>
    </div>
  </div>
)

const RenderLineContent = (line: CopilotEducationLine) => (
  <div className="">
    <div className="fs-md fw-semibold text-dark">{line.institution}</div>
    {maybeHtml(line.description, (description) => (
      <div className="fs-md text-gray-600 mt-1">{description}</div>
    ))}
    {maybeHtml(line.startYear, () => (
      <div className="fs-md text-gray-600 mt-1">
        {formatEducationDates(line)}
      </div>
    ))}
  </div>
)

enum Modal
  { NoModal
  , AddEducationModal
  , EditEducationModal
  , ConfirmDeleteEducationModal
  }

export const Edit = (props: CopilotProfileProps, onUpdate: (CopilotProfileProps) => void) => {
  const [isSaving, setIsSaving] = useState(false);

  const [modal, setModal] = useState(Modal.NoModal);

  const [lineToEdit, setLineToEdit] = useState<CopilotEducationLine>();

  const [institution, setInstitution] = useState<string>();
  const [description, setDescription] = useState<string>();
  const [startMonth, setStartMonth] = useState<number>();
  const [startYear, setStartYear] = useState<number>();
  const [endMonth, setEndMonth] = useState<number>();
  const [endYear, setEndYear] = useState<number>();

  const [showErrors, setShowErrors] = useState(false);

  function clearStateLineFields() {
    setLineToEdit(undefined);
    setInstitution(undefined);
    setDescription(undefined);
    setStartMonth(undefined);
    setStartYear(undefined);
    setEndMonth(undefined);
    setEndYear(undefined);
  }

  function startEditingLine(line: CopilotEducationLine) {
    setLineToEdit(line);
    setInstitution(line.institution);
    setDescription(line.description);
    setStartMonth(line.startMonth);
    setStartYear(line.startYear);
    setEndMonth(line.endMonth);
    setEndYear(line.endYear);

    setModal(Modal.EditEducationModal);
  }

  function validateInputs() {
    const isValid =
      !(endYear &&
        startYear &&
        (endYear < startYear || (endYear === startYear && endMonth && startMonth && endMonth < startMonth))) &&
      institution.length > 0;

    if (!isValid) {
      setShowErrors(true);
    }

    return isValid;
  }

  function addEducationLine() {
    if (!validateInputs()) return;

    setIsSaving(true);

    const postBody = {
      institution: institution,
      description: description,
      startMonth: startMonth,
      startYear: startYear,
      endMonth: endMonth,
      endYear: endYear
    }

    API.post("api_copilot_add_education_line_path", postBody).then(function (result) {
      onUpdate(result);
      setModal(Modal.NoModal);
      clearStateLineFields();
      setIsSaving(false);
    })
  }

  function deleteEducationLine() {
    setIsSaving(true);

    const postBody = { id: lineToEdit.id }

    API.post("api_copilot_delete_education_line_path", postBody).then(function (result) {
      onUpdate(result);
      if (modal === Modal.ConfirmDeleteEducationModal) {
        setModal(Modal.NoModal);
        setIsSaving(false);
      } else if (modal === Modal.EditEducationModal) {
        // Don't do anything — we've deleted the original version of the line, but we still need to add the new one.
      }
    })
  }

  function saveEdits() {
    if (!validateInputs()) return;

    deleteEducationLine();
    addEducationLine();
  }

  const ViewModal = () => {
    const InputBody = () => (
      <div className="">
        <div className="mt-2">
          <h5 className="mb-1">Educational Institution/School*</h5>
          <input
            onChange={(e) => setInstitution(e.target.value)}
            className="form-control t--institution"
            value={institution}
            placeholder='i.e. Cornell University'
          />
          {htmlIf(showErrors && !(institution.length > 0),
            <div className="text-danger fw-semibold mt-1">
              Please enter an institution or school name.
            </div>
          )}
        </div>
        <div className="mt-2">
          <h5 className="mb-1">Description*</h5>
          <input
            onChange={(e) => setDescription(e.target.value)}
            className="form-control t--description"
            value={description}
            placeholder='i.e. B.S. in Communications'
          />
        </div>
        <div className="mt-2">
          <h5 className="mb-1">Start Date</h5>
          <MonthYearSelect
            month={startMonth}
            year={startYear}
            setMonth={setStartMonth}
            setYear={setStartYear}
          />
        </div>
        <div className="mt-2">
          <h5 className="mb-1">End Date</h5>
          <MonthYearSelect
            month={endMonth}
            year={endYear}
            setMonth={setEndMonth}
            setYear={setEndYear}
          />
          {/* Courtesy of ChatGPT */}
          {htmlIf(
            (endYear && startYear &&
              (endYear < startYear || (endYear === startYear && endMonth && startMonth && endMonth < startMonth))
            ),
            <div className="text-danger fw-semibold mt-1">
              End date cannot be before start date!
            </div>
          )}
        </div>
      </div>
    )

    switch(modal) {
      case Modal.NoModal:
        return null
      case Modal.AddEducationModal:
        return (
          <DismissibleModal
            dialogClass='modal-lg'
            title={<h4>Education</h4>}
            body={InputBody()}
            footer={
              <div>
                <button className="btn btn-primary px-4 t--save-education-line" onClick={addEducationLine} disabled={isSaving}>Save</button>
              </div>
            }
            onDismiss={() => setModal(Modal.NoModal)}
          />
        )
      case Modal.EditEducationModal:
        return (
          <DismissibleModal
            dialogClass='modal-lg'
            title={<h4>Education</h4>}
            body={InputBody()}
            footer={
              <div>
                <button className="btn btn-primary px-4 t--edit-education-line" onClick={saveEdits} disabled={isSaving}>Save</button>
              </div>
            }
            onDismiss={() => {
              setModal(Modal.NoModal);
              clearStateLineFields();
            }}
          />
        )
      case Modal.ConfirmDeleteEducationModal:
        return (
          <DismissibleModal
            title={<h4>Confirm Removal</h4>}
            body={
              <div>
                <div className="mb-2">
                  Are you sure you’d like to delete this education line from your profile?
                </div>
                <div className="">
                  <button className="btn btn-danger w-100 mb-2" onClick={deleteEducationLine}>Remove</button>
                  <button className="btn btn-outline-primary w-100" onClick={() => setModal(Modal.NoModal)}>Keep</button>
                </div>
              </div>
            }
            onDismiss={() => setModal(Modal.NoModal)}
          />
        )
    }
  }

  return (
    <div className="card mt-3 rounded-2">
      {ViewModal()}
      <div className="card-body py-3">
        <div className="d-flex align-items-center justify-content-between">
          <div className="fs-xl fw-bold text-dark">Education history</div>
          <button className="btn btn-link p-0 t--edit-skills" onClick={() => setModal(Modal.AddEducationModal)}>
            <i className="ai-circle-plus me-1" />
            Add education
          </button>
        </div>
        {props.educationHistory.length > 0
        ? <div className="mt-2">
            <PaginatedItems
              allItems={sortEducationHistory(props.educationHistory)}
              numItemsPerPage={5}
              renderItem={(line) => (
                <div className="py-2 border-bottom" key={`education-line-${line.id}`}>
                  <div className="d-flex align-items-start justify-content-between">
                    {RenderLineContent(line)}
                    <div className="d-flex">
                      <button className="btn btn-link p-0 me-2" onClick={() => startEditingLine(line)}>
                        <i className="ai-edit fs-xl" />
                      </button>
                      <button className="btn btn-link text-danger p-0"
                        onClick={() => {
                          setModal(Modal.ConfirmDeleteEducationModal);
                          setLineToEdit(line)
                        }}
                      >
                        <i className="ai-trash fs-xl" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            />
          </div>
        : <div className="text-center py-2 mt-2 text-dark">
            <img src='/img/dashboard/receipt.png' width={64} className="" />
            <div className="mt-1 fs-md fw-semibold">Add education history</div>
            <div className="fs-md">Showcase your background and strengthen your profile.</div>
          </div>
        }
      </div>
    </div>
  )
}
